import React, { useState, useRef, useEffect } from 'react'
import type { StudyWithCounts } from '@lura/shared'

interface StudySelectorProps {
  studies: StudyWithCounts[]
  selectedStudy?: StudyWithCounts | null
  onSelect: (study: StudyWithCounts | null) => void
  placeholder?: string
  disabled?: boolean
}

export const StudySelectorForDevice: React.FC<StudySelectorProps> = ({
  studies,
  selectedStudy = null,
  onSelect,
  placeholder = 'Choose a Study...',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Filter studies based on search query
  const filteredStudies = studies.filter(study => {
    const searchLower = searchQuery.toLowerCase()
    const title = study.title.toLowerCase()
    const description = study.description?.toLowerCase() || ''
    return title.includes(searchLower) || description.includes(searchLower)
  })

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setSearchQuery('')
        setFocusedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  const handleSelect = (study: StudyWithCounts | null) => {
    onSelect(study)
    setIsOpen(false)
    setSearchQuery('')
    setFocusedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(prev =>
          prev < filteredStudies.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0))
        break
      case 'Enter':
        e.preventDefault()
        if (focusedIndex >= 0 && filteredStudies[focusedIndex]) {
          handleSelect(filteredStudies[focusedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSearchQuery('')
        setFocusedIndex(-1)
        break
    }
  }

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`w-full text-left px-4 py-3 bg-white border-2 rounded-xl transition-all duration-200 ${
          isOpen
            ? 'border-primary-500 shadow-lg'
            : 'border-gray-300 hover:border-gray-400'
        } focus:outline-none focus:border-primary-500 focus:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <div className="flex items-center justify-between">
          {selectedStudy ? (
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900">{selectedStudy.title}</div>
              {selectedStudy.description && (
                <div className="text-sm text-gray-500 mt-0.5 truncate">
                  {selectedStudy.description}
                </div>
              )}
            </div>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${
              isOpen ? 'transform rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-primary-500 rounded-xl shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="p-4 border-b border-gray-200">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value)
                setFocusedIndex(-1)
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search studies..."
              className="w-full px-3 py-2 border-2 border-primary-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Studies List */}
          <div className="max-h-64 overflow-y-auto">
            {/* No Study option - always show unless searching */}
            {!searchQuery && (
              <button
                type="button"
                onClick={() => handleSelect(null)}
                onMouseEnter={() => setFocusedIndex(-1)}
                className="w-full text-left px-4 py-3 transition-colors duration-150 hover:bg-gray-50 text-gray-500 border-b border-gray-100"
              >
                <div className="font-medium">No Study</div>
              </button>
            )}
            {filteredStudies.length === 0 && searchQuery ? (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                No studies found matching your search
              </div>
            ) : (
              filteredStudies.map((study, index) => (
                <button
                  key={study.id}
                  type="button"
                  onClick={() => handleSelect(study)}
                  onMouseEnter={() => setFocusedIndex(index)}
                  className={`w-full text-left px-4 py-3 transition-colors duration-150 ${
                    index === focusedIndex
                      ? 'bg-primary-50 text-primary-900'
                      : 'hover:bg-gray-50 text-gray-900'
                  } ${index < filteredStudies.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="font-medium">{study.title}</div>
                  {study.description && (
                    <div className="text-sm text-gray-500 mt-0.5">
                      {study.description}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
